import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Users, DollarSign, Calendar, Clock, Wrench, RefreshCw,
  ChevronRight, MapPin, AlertTriangle, Settings, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

function formatCurrency(n) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0); }

function timeSince(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "à l'instant";
  if (diff < 60) return `il y a ${Math.round(diff)} min`;
  if (diff < 1440) return `il y a ${Math.round(diff / 60)}h`;
  return `il y a ${Math.round(diff / 1440)}j`;
}

export default function ExploitationDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/exploitation/dashboard');
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    // Auto-sync on every page open then fetch dashboard
    const syncAndFetch = async () => {
      try {
        await api.post('/exploitation/sync');
      } catch (e) { console.warn('Auto-sync failed:', e); }
      fetchDashboard();
    };
    syncAndFetch();
  }, [api, fetchDashboard]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/exploitation/sync');
      toast.success(res.data.message);
      await fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation');
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  const g = data?.global || {};
  const sites = data?.sites || [];
  const lastSync = data?.last_sync;

  const statItems = [
    { label: 'Clients', value: g.clients_actifs || 0, icon: Users, color: '#FFFFFF' },
    { label: 'CA saison', value: formatCurrency(g.ca_saison), icon: DollarSign, color: '#00E01A' },
    { label: 'Sessions', value: g.sessions_saison || 0, icon: Calendar, color: '#3B82F6' },
    { label: 'En attente', value: formatCurrency(g.paiements_attente_amount), icon: Clock, color: '#F59E0B' },
    { label: 'SAV ouverts', value: g.sav_ouverts || 0, icon: Wrench, color: g.sav_ouverts > 0 ? '#EF4444' : '#00E01A' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exploitation-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Exploitation</h1>
          <p className="text-[#8C8F94] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Vue d'ensemble multi-sites
            {lastSync && <span className="ml-2 text-[#52555A]">- Dernière sync : {timeSince(lastSync)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/exploitation/clients" className="flex items-center gap-1.5 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-3 py-2 rounded-full text-xs transition-all" data-testid="nav-clients">
            <UserPlus size={14} /> Clients
          </Link>
          <Link to="/exploitation/settings" className="flex items-center gap-1.5 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-3 py-2 rounded-full text-xs transition-all" data-testid="nav-settings">
            <Settings size={14} /> Sites
          </Link>
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2 rounded-full text-xs transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,224,26,0.2)]" data-testid="sync-btn">
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Synchronisation...' : 'Synchroniser'}
          </button>
        </div>
      </div>

      {/* Global stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="global-stats">
        {statItems.map(s => (
          <div key={s.label} className="bg-[#111111] rounded-xl border border-[#222] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
              <s.icon size={20} style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{s.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Per-site cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="site-cards">
        {sites.map(site => (
          <div key={site.site_id || site.id} className="bg-[#111111] rounded-xl border border-[#222] p-5 hover:border-[#00E01A]/30 transition-all" data-testid={`site-card-${site.site_id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00E01A]/10 flex items-center justify-center">
                  <MapPin size={20} className="text-[#00E01A]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{site.name}</h3>
                  <p className="text-[#8C8F94] text-xs">{site.city}{site.address ? ` — ${site.address}` : ''}</p>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${site.active ? 'bg-[#142915] text-[#00E01A]' : 'bg-[#2C3338] text-[#8C8F94]'}`}>
                {site.active ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-[#0a0a0a] rounded-lg p-3 text-center">
                <p className="text-[#00E01A] font-bold text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatCurrency(site.ca)}</p>
                <p className="text-[#8C8F94] text-[10px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>CA</p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-3 text-center">
                <p className="text-white font-bold text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{site.clients_actifs}</p>
                <p className="text-[#8C8F94] text-[10px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Clients</p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-3 text-center">
                <p className="text-white font-bold text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{site.sessions}</p>
                <p className="text-[#8C8F94] text-[10px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sessions</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex gap-3">
                {site.paiements_attente > 0 && (
                  <span className="flex items-center gap-1 text-[#F59E0B]"><Clock size={12} /> {formatCurrency(site.paiements_attente)}</span>
                )}
                {site.sav_ouverts > 0 && (
                  <span className="flex items-center gap-1 text-[#EF4444]"><AlertTriangle size={12} /> {site.sav_ouverts} SAV</span>
                )}
              </div>
              <Link to={`/exploitation/site/${site.site_id}`} className="flex items-center gap-1 text-[#00E01A] hover:text-[#00B815] transition-colors font-medium" data-testid={`detail-${site.site_id}`}>
                Voir le détail <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {sites.length === 0 && (
        <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
          <MapPin size={40} className="mx-auto text-[#52555A] mb-3" />
          <p className="text-[#8C8F94] mb-3">Aucun site. Cliquez sur "Synchroniser" pour importer les données du booking.</p>
          <button onClick={handleSync} disabled={syncing} className="bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full text-sm transition-all" data-testid="first-sync-btn">
            <RefreshCw size={14} className={`inline mr-2 ${syncing ? 'animate-spin' : ''}`} /> Première synchronisation
          </button>
        </div>
      )}
    </div>
  );
}
